import { Project, ProjectFormData, ProjectStatus } from '../types/Project';
import { makeAuthenticatedRequest } from './authService';
import { logApiConfig } from '../config/api';

// Log API configuration in development
if (process.env.NODE_ENV === 'development') {
  logApiConfig();
}

export interface CreateProjectData {
  user_id: string;
  name: string;
  description?: string;
  status?: string;
  start_date?: string;
  target_date?: string;
  progress_percentage?: number;
  notes?: string;
}

export interface UpdateProjectData {
  id: string;
  name?: string;
  description?: string;
  status?: string;
  start_date?: string;
  target_date?: string;
  progress_percentage?: number;
  notes?: string;
}

export interface ProjectFilters {
  user_id: string;
  status?: string;
  search_term?: string;
  limit?: number;
  offset?: number;
}

class ProjectService {
  // Create a new project
  async createProject(userId: string, projectData: ProjectFormData): Promise<{ success: boolean; message?: string }> {
    try {
      const data: CreateProjectData = {
        user_id: userId,
        name: projectData.name,
        description: projectData.description || undefined,
        status: projectData.status || ProjectStatus.NOT_STARTED,
        start_date: projectData.start_date || undefined,
        target_date: projectData.target_date || undefined,
        progress_percentage: projectData.progress_percentage || 0,
        notes: projectData.notes || undefined
      };

      const response = await makeAuthenticatedRequest('/projects', {
        method: 'POST',
        body: JSON.stringify(data)
      });
      
      return response;
    } catch (error) {
      console.error('Error creating project:', error);
      throw error;
    }
  }

  // Get project by ID
  async getProjectById(projectId: string): Promise<{ success: boolean; project?: any }> {
    try {
      const response = await makeAuthenticatedRequest(`/projects/${projectId}`, {
        method: 'GET'
      });
      
      return response;
    } catch (error) {
      console.error('Error fetching project:', error);
      throw error;
    }
  }

  // Get all projects for a user
  async getUserProjects(filters: ProjectFilters): Promise<{ success: boolean; projects?: any[]; count?: number }> {
    try {
      // Build query parameters
      const queryParams = new URLSearchParams();
      if (filters.status) queryParams.append('status', filters.status);
      if (filters.limit) queryParams.append('limit', filters.limit.toString());
      if (filters.offset) queryParams.append('offset', filters.offset.toString());
      
      const queryString = queryParams.toString();
      const url = `/projects/user/${filters.user_id}${queryString ? `?${queryString}` : ''}`;
      
      const response = await makeAuthenticatedRequest(url, {
        method: 'GET'
      });
      
      return response;
    } catch (error) {
      console.error('Error fetching user projects:', error);
      throw error;
    }
  }

  // Update a project
  async updateProject(updateData: UpdateProjectData): Promise<{ success: boolean; message?: string }> {
    try {
      const { id, ...dataToUpdate } = updateData;
      
      const response = await makeAuthenticatedRequest(`/projects/${id}`, {
        method: 'PUT',
        body: JSON.stringify(dataToUpdate)
      });
      
      return response;
    } catch (error) {
      console.error('Error updating project:', error);
      throw error;
    }
  }

  // Delete a project
  async deleteProject(projectId: string): Promise<{ success: boolean; message?: string }> {
    try {
      const response = await makeAuthenticatedRequest(`/projects/${projectId}`, {
        method: 'DELETE'
      });
      
      return response;
    } catch (error) {
      console.error('Error deleting project:', error);
      throw error;
    }
  }

  // Search projects
  async searchProjects(filters: ProjectFilters): Promise<{ success: boolean; projects?: any[]; count?: number }> {
    try {
      // Build query parameters
      const queryParams = new URLSearchParams();
      queryParams.append('user_id', filters.user_id);
      if (filters.status) queryParams.append('status', filters.status);
      if (filters.search_term) queryParams.append('search_term', filters.search_term);
      if (filters.limit) queryParams.append('limit', filters.limit.toString());
      if (filters.offset) queryParams.append('offset', filters.offset.toString());
      
      const response = await makeAuthenticatedRequest(`/projects/search?${queryParams.toString()}`, {
        method: 'GET'
      });
      
      return response;
    } catch (error) {
      console.error('Error searching projects:', error);
      throw error;
    }
  }

  // Get tasks for a specific project
  async getProjectTasks(projectId: string, filters?: { status?: string }): Promise<{ success: boolean; tasks?: any[]; count?: number }> {
    try {
      // Build query parameters
      const queryParams = new URLSearchParams();
      if (filters?.status) queryParams.append('status', filters.status);
      
      const queryString = queryParams.toString();
      const url = `/projects/${projectId}/tasks${queryString ? `?${queryString}` : ''}`;
      
      const response = await makeAuthenticatedRequest(url, {
        method: 'GET'
      });
      
      return response;
    } catch (error) {
      console.error('Error fetching project tasks:', error);
      throw error;
    }
  }

  // Convert database project to our Project interface
  convertDatabaseProject(dbProject: any): Project {
    return {
      id: String(dbProject.id),
      user_id: dbProject.user_id,
      name: dbProject.name,
      description: dbProject.description || undefined,
      status: dbProject.status as ProjectStatus,
      start_date: dbProject.start_date || undefined,
      target_date: dbProject.target_date || undefined,
      completed_date: dbProject.completed_date || undefined,
      progress_percentage: dbProject.progress_percentage || 0,
      notes: dbProject.notes || undefined,
      created_at: dbProject.created_at,
      updated_at: dbProject.updated_at
    };
  }
}

export const projectService = new ProjectService();

